import { Account, User, Opportunity, OpportunityStage } from "../../types";
import { stageOrder } from "../constants";

/**
 * Technical governance and expert gating logic.
 * Guided by the 45-member mixed panel to ensure architectural and regulatory alignment.
 */

export function getNextStage(currentStage: OpportunityStage): OpportunityStage {
  if (isClosedStage(currentStage)) {
    return currentStage;
  }
  const currentIndex = stageOrder.indexOf(currentStage);
  if (currentIndex === -1 || currentIndex === stageOrder.length - 1) {
    return currentStage;
  }
  return stageOrder[currentIndex + 1];
}

export function isClosedStage(stage: OpportunityStage): boolean {
  return stage === "closed_won" || stage === "closed_lost";
}

export type GovernanceOverlay = {
  text: string;
  expert: string;
  severity: "info" | "warning" | "block";
};

export function getGovernanceOverlays(
  account: Account,
  user: User,
  opportunity: Opportunity,
): GovernanceOverlay[] {
  const overlays: GovernanceOverlay[] = [];

  // CTO / Principal: Enterprise & Government Rigor
  if (account.account_type === "government") {
    overlays.push({
      text: "Government account: route legal, accessibility, and procurement questions to staffed reviewers.",
      expert: "Government Liaison",
      severity: "warning",
    });
  }

  // Delivery Governance: Managed Service Commitment
  if (user.sponsorship_type === "managed_service") {
    overlays.push({
      text: "Managed-service overlay: staff queues and review ownership are part of the product promise.",
      expert: "Delivery Governance",
      severity: "info",
    });
  }

  // Data Privacy Lead: Regional Compliance (EU Data Sovereignty)
  if (user.region.includes("EU")) {
    overlays.push({
      text: "Region-sensitive privacy review is required before making stronger cross-border data claims.",
      expert: "Data Privacy Lead",
      severity: "block",
    });
  }

  // Security Auditor: Stage-Specific External Release Block
  if (
    opportunity.current_stage === "offer_review" ||
    opportunity.current_stage === "outreach_ready"
  ) {
    overlays.push({
      text: "External use blocked until explicit approval is recorded.",
      expert: "Security Auditor",
      severity: "block",
    });
  }

  // Systems Architect: Architecture Alignment
  if (opportunity.employment_type === "1099" && account.account_type === "enterprise") {
    overlays.push({
      text: "Enterprise 1099 posture detected. Ensure SOW and liability architecture is verified.",
      expert: "Systems Architect",
      severity: "warning",
    });
  }

  return overlays;
}

export function getHealthCheckOverlays(
  account: Account | undefined,
  user: User | undefined,
  opportunity: Opportunity | undefined,
): string[] {
  if (!account || !user || !opportunity) return [];
  return getGovernanceOverlays(account, user, opportunity).map((o) => o.text);
}
